// Arrow-in-circle affordance for primary CTAs. On hover of the parent
// (which must carry the `group` class), the arrow exits right while a clone
// re-enters from the left (Codrops ButtonHoverStyles, example 12 "skoll")
// and the circle fill brightens. Transform-only; static under reduced motion.

import { ArrowRight } from "iconoir-react";

import { cn } from "@/lib/utils";

export type CtaArrowCircleTone = "onPulse" | "onWhite";

const toneClassName: Record<CtaArrowCircleTone, string> = {
  /** Semi-transparent disc on pulse-blue buttons (homepage hero reference). */
  onPulse: "bg-white/20 group-hover:bg-white/30",
  /** Solid pulse disc on white pill buttons (footer CTA reference). */
  onWhite: "bg-pulse-700 group-hover:bg-pulse-600",
};

interface CtaArrowCircleProps {
  className?: string;
  iconClassName?: string;
  strokeWidth?: number;
  /** Circle treatment — matches the parent button surface. */
  tone?: CtaArrowCircleTone;
}

export function CtaArrowCircle({
  className,
  iconClassName,
  strokeWidth = 1.5,
  tone = "onPulse",
}: CtaArrowCircleProps) {
  const arrowClass = cn(
    "size-4 text-white transition-transform duration-300 ease-[cubic-bezier(0.7,0,0.2,1)]",
    iconClassName
  );

  return (
    <span
      aria-hidden
      className={cn(
        "relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full transition-colors",
        toneClassName[tone],
        className
      )}
    >
      <ArrowRight
        className={cn(arrowClass, "motion-safe:group-hover:translate-x-[200%]")}
        strokeWidth={strokeWidth}
      />
      <ArrowRight
        className={cn(
          arrowClass,
          "absolute -translate-x-[200%] motion-safe:group-hover:translate-x-0"
        )}
        strokeWidth={strokeWidth}
      />
    </span>
  );
}
